<template>
  <div>
    <div
      class="application-wrapper"
      @click="
        emit('createCustomApplication', props.application, connectSettings)
      ">
      <div class="tool-desc">
        <!-- Favicon of the tools -->
        <el-image
          lazy
          class="application-image"
          :src="`https://logos.saasmetrix.io/${encodeURIComponent(props.application.name)}.png`">
          <template #error>
            <ImageOff :size="30" />
          </template>
        </el-image>
        <!-- Name of the tool -->
        <div class="tool-desc-text">
          <div style="display: flex; align-items: center; gap: 10px">
            <span class="application-name">{{
              props.application.display_name
            }}</span>

            <!-- Show settings icon -->
            <div class="settings-icon">
              <Settings
                :size="12"
                @click.prevent.stop="handleConnectSettings" />
            </div>
          </div>
          <!-- Type of Tool OAuth/Bot/Template -->
          <div class="application-type">
            <span v-if="props.application.subtitle">
              {{ props.application.subtitle }} |
            </span>
            <span>{{ translatedType }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -> Click to activate/add Software -->
      <div class="center-create-button">
        <el-image
          v-if="
            props.application.type?.toLowerCase() === 'oauth' &&
            props.application.name === 'Google'
          "
          src="https://logos.saasmetrix.io/btn_google_signin_light_normal_web@2x.png"
          style="height: 36px; width: 150px" />
        <SmButton
          v-else
          size="small"
          :disabled="props.isDisabled || isDemoMode"
          @click.stop="
            emit('createCustomApplication', props.application, connectSettings)
          ">
          {{ i18n.t('drawers.addApplicationDrawer.button.connect') }}
        </SmButton>
      </div>
    </div>

    <!-- Settings -->
    <div
      :class="['application-add-item-settings', { show: showConnectSettings }]"
      @click.stop>
      <div>
        <div class="application-add-item-settings__content">
          <div class="application-add-item-settings__content__title">
            {{ i18n.t('connectionSettings') }}
          </div>

          <div class="application-add-item-settings__content__settings">
            <div>{{ i18n.t('readonly') }}:</div>
            <SmSwitch
              v-model="connectSettings.readonly"
              style="margin-left: auto" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'

  import { SoftwareInformation } from '@/client'
  import { ImageOff, Settings } from 'lucide-vue-next'
  const i18n = useI18n()

  const isDemoMode = computed(() => false)

  const props = defineProps({
    application: {
      type: Object as PropType<SoftwareInformation>,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  })

  export interface ConnectSettings {
    readonly: boolean
  }

  const emit = defineEmits<{
    closeDrawer: []
    createCustomApplication: [
      application: SoftwareInformation,
      settings: ConnectSettings,
    ]
  }>()

  const translatedType = computed(() =>
    i18n.t(`general.applicationTypeTag.${props.application.type || 'template'}`)
  )
  const showConnectSettings = ref(false)
  const connectSettings = ref<ConnectSettings>({
    readonly: false,
  })
  function handleConnectSettings() {
    showConnectSettings.value = !showConnectSettings.value
  }
</script>

<style lang="scss" scoped>
  .paragraph {
    word-wrap: break-word;
  }

  .application-wrapper .settings-icon {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  .application-wrapper:hover .settings-icon {
    opacity: 1;
  }
</style>

<style>
  .application-add-item-settings {
    display: grid;
    grid-template-rows: 0fr;

    transition: grid-template-rows 0.2s ease-in-out;
  }

  .application-add-item-settings > div {
    overflow: hidden;
  }

  .application-add-item-settings.show {
    grid-template-rows: 1fr;
  }

  .application-add-item-settings__content {
    padding: 10px;
    margin-left: calc(40px);
    padding-left: 1rem;

    hr {
      border-top: 0.5px solid theme('colors.gray[500]');
    }
  }

  .application-add-item-settings__content__title {
    margin-bottom: 1px;
    font-weight: 500;
  }

  .application-add-item-settings__content__settings {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    align-items: center;

    .sm-switch {
      font-size: 0.85rem;
    }
  }
</style>
